import TableHeader from '@/components/product-table/TableHeader';
import TableRow from '@/components/product-table/TableRow';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { useAddToCart } from '@/hooks/useAddToCart';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useAuth } from '@/stores/authStore';
import { useTableStore } from '@/stores/useTableStore';
import { ColumnId, LaravelPaginator, ProductListItem } from '@/types';
import { InfiniteScroll, router } from '@inertiajs/react';
import { Columns } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { route } from 'ziggy-js';

export interface ColumnDef {
    id: ColumnId;
    slug?: string;
    label: string;
    className?: string;
    headerClassName?: string;
    sortable?: boolean;
}

const ALL_COLUMNS: ColumnDef[] = [
    // { id: 'photo', label: 'Фото', className: 'w-16', sortable: false },
    {
        id: 'name',
        slug: 'title',
        label: 'Название',
        className: 'w-[300px]',
        sortable: true,
    },
    {
        id: 'brewery',
        slug: 'brewery',
        label: 'Пивоварня',
        className: 'w-[100px]',
        sortable: true,
    },
    { id: 'style', label: 'Стиль', className: 'max-w-[150px]', sortable: true },
    { id: 'untappd', label: 'Рейтинг', sortable: true },
    {
        id: 'packaging_type',
        label: 'Тара',
        className: 'w-24',
        sortable: true,
    },
    {
        id: 'volume',
        label: 'Объем',
        className: 'w-22 whitespace-nowrap',
        sortable: true,
    },
    {
        id: 'props',
        label: 'Хар-ки',
        className: 'min-w-[100px]',
        sortable: false,
    },
    {
        id: 'price',
        label: 'Цена',
        className: 'w-22 whitespace-nowrap',
        sortable: true,
    },
    {
        id: 'actions',
        label: 'Купить',
        headerClassName: 'text-right',
        className: 'text-right min-w-[100px]',
        sortable: false,
    },
];

interface ProductTableProps {
    variations: LaravelPaginator<ProductListItem>;
}

export function ProductTable({ variations }: ProductTableProps) {
    const { visibleColumns, toggleColumn } = useTableStore();
    const isMobile = useIsMobile();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const { isAuthenticated } = useAuth();

    const activeColumns = useMemo(
        () =>
            ALL_COLUMNS.filter(
                (col) =>
                    visibleColumns.includes(col.id) &&
                    !(col.id === 'actions' && !isAuthenticated),
            ),
        [visibleColumns, isAuthenticated],
    );

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const addToCartHandler = useAddToCart();

    const handleProductClick = useCallback((slug: string) => {
        router.get(route('product', { slug }));
    }, []);

    return (
        <div className="space-y-4">
            {/* Column selector for Desktop */}
            <div className="relative hidden justify-end md:flex">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="gap-2 rounded-md border-zinc-800 bg-zinc-900 text-zinc-400 hover:text-zinc-100"
                    aria-haspopup="true"
                    aria-expanded={isMenuOpen}
                >
                    <Columns className="h-4 w-4" />
                    <span className="font-medium">Столбцы</span>
                </Button>

                {isMenuOpen && (
                    <div
                        ref={menuRef}
                        className="animate-in fade-in zoom-in-95 absolute right-0 top-10 z-20 w-56 rounded-md border border-zinc-800 bg-zinc-950 p-4 shadow-2xl duration-200"
                    >
                        <h4 className="mb-3 border-b border-zinc-800 pb-2 text-sm font-bold uppercase tracking-wide text-brand-mist">
                            Отображение
                        </h4>
                        <div className="space-y-2.5">
                            {ALL_COLUMNS.filter((col) => col.id != 'name').map(
                                (col) => (
                                    <Checkbox
                                        key={col.id}
                                        id={`col-${col.id}`}
                                        label={col.label}
                                        checked={visibleColumns.includes(
                                            col.id,
                                        )}
                                        onChange={() => toggleColumn(col.id)}
                                    />
                                ),
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Mobile-optimized List View */}

            {isMobile ? (
                <InfiniteScroll
                    data="products"
                    buffer={30}
                    onlyNext={true}
                    loading={() => 'Загрузка...'}
                    className="space-y-3 md:hidden"
                >
                    {variations.data.map((variation) => (
                        <TableRow
                            key={variation.id}
                            type="mobile"
                            variation={variation}
                            onAddToCart={addToCartHandler}
                            onProductClick={handleProductClick}
                            isAuthenticated={isAuthenticated}
                        />
                    ))}
                </InfiniteScroll>
            ) : (
                <div className="hidden w-full overflow-x-auto rounded-md border border-zinc-800 bg-zinc-900/30 backdrop-blur-sm md:block">
                    <InfiniteScroll
                        data="products"
                        itemsElement="#table-body"
                        startElement="#table-header"
                        endElement="#table-footer"
                        buffer={30}
                        onlyNext={true}
                        loading={() => 'Загрузка...'}
                    >
                        {({ loading }) => (
                            <table className="w-full border-collapse text-left text-sm text-zinc-400">
                                <TableHeader
                                    theadId={'table-header'}
                                    activeColumns={activeColumns}
                                />

                                <tbody
                                    id="table-body"
                                    className="divide-y divide-zinc-800/50"
                                >
                                    {variations.data.map((variation) => (
                                        <TableRow
                                            key={variation.id}
                                            type="desktop"
                                            variation={variation}
                                            activeColumns={activeColumns}
                                            onAddToCart={addToCartHandler}
                                            onProductClick={handleProductClick}
                                            isAuthenticated={isAuthenticated}
                                        />
                                    ))}
                                </tbody>

                                <tfoot id="table-footer">
                                    <tr>
                                        <td className="p-4">
                                            {loading ? 'Загрузка...' : ''}
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </InfiniteScroll>
                </div>
            )}
        </div>
    );
}
